<script lang="ts" setup generic="T extends object = Record<string, unknown>, TContext = unknown">
import type { Component, VNodeChild } from 'vue';
import type {
	FormSchema,
	FormSchemaDataField,
	FormSchemaResolved,
} from '#layers/block-engine/types/form-schema';
import type { FormSchemaAdapter } from '#layers/block-engine/utils/form-schema/adapter';
import type { FormSchemaSectionsRenderEntry } from './form-schema-rendering';
import { createReusableTemplate } from '@vueuse/core';
import { computed, h } from 'vue';

import { resolveFormSchema } from '#layers/block-engine/utils/form-schema/schema';
import {
	createFormSchemaRenderEntry,
} from './form-schema-rendering';
import { RenderFnWrapper } from './RenderFnWrapper';

const props = defineProps<{
	schema: FormSchema<T, TContext>;
	adapter: FormSchemaAdapter<T>;
	context?: TContext;
	transformSchema?: (schema: FormSchemaResolved<T>) => FormSchemaResolved<T>;
}>();

defineSlots<{
	'field-hint': (props: { field: FormSchemaDataField<T, string> }) => unknown;
	'field': (props: { field: FormSchemaDataField<T, string> }) => unknown;
	'field-render': (props: {
		field: FormSchemaDataField<T, string>;
		render: Component;
		value: unknown;
		setValue: (value: unknown) => void;
	}) => unknown;
}>();

/**
 * Forward a field write to the adapter that owns the form values.
 */
function handleUpdate(field: FormSchemaDataField<T, string>, value: unknown) {
	props.adapter.set({
		field,
		value,
	});
}

const resolvedSchema = computed(() => {
	const values = props.adapter.getValues();

	if (typeof props.schema === 'function') {
		return resolveFormSchema({
			schema: props.schema,
			transformSchema: props.transformSchema,
			context: (props.context ?? {}) as TContext,
			values,
		});
	}

	return resolveFormSchema({
		schema: props.schema,
		transformSchema: props.transformSchema,
		values,
	});
});

const renderEntries = computed(() => resolvedSchema.value.flatMap((field, index) => {
	const entry = createFormSchemaRenderEntry(field, index);

	return entry ? [entry] : [];
}));

// Reuse one recursive template so every nesting level keeps the same adapter, context, and field slots.
const [DefineNestedFields, NestedFields] = createReusableTemplate<{
	nested: {
		schema: FormSchemaResolved<T>;
	};
}>();

/**
 * Render the configured shared slot content for a sections chrome block.
 */
function renderSingleSlot(entry: FormSchemaSectionsRenderEntry<T>, slotProps: Record<string, unknown>): VNodeChild {
	return entry.singleSlot?.render({
		sections: entry.sections,
		slotProps,
		renderSection,
		renderSections: (sections) => renderSections(entry, sections),
	});
}

/**
 * Render one normalized section through the nested FormSchema renderer.
 */
function renderSection(section: FormSchemaSectionsRenderEntry<T>['sections'][number]): VNodeChild {
	return h(NestedFields, {
		key: section.slotName,
		nested: {
			schema: section.section.fields,
		},
	});
}

/**
 * Render normalized sections through the nested FormSchema renderer.
 */
function renderSections(
	entry: FormSchemaSectionsRenderEntry<T>,
	sections: readonly FormSchemaSectionsRenderEntry<T>['sections'][number][] = entry.sections,
): VNodeChild {
	return sections.map((section) => renderSection(section));
}
</script>

<template>
	<DefineNestedFields v-slot="{ nested }">
		<DFormLayout class="my-4">
			<FormSchemaFields
				:schema="nested.schema"
				:adapter
				:context
				:transform-schema
			>
				<template #field-hint="slotProps">
					<slot name="field-hint" v-bind="slotProps" />
				</template>

				<template #field="slotProps">
					<slot name="field" v-bind="slotProps" />
				</template>

				<template #field-render="slotProps">
					<slot name="field-render" v-bind="slotProps" />
				</template>
			</FormSchemaFields>
		</DFormLayout>
	</DefineNestedFields>

	<template
		v-for="entry of renderEntries"
	>
		<FormSchemaField
			v-if="entry.type === 'data'"
			:key="`data-${entry.key}`"
			:field="entry.field"
			:adapter
			@set-value="(value) => handleUpdate(entry.field, value)"
		>
			<template #hint>
				<slot
					name="field-hint"
					:field="entry.field"
				/>
			</template>

			<template #default>
				<slot
					name="field"
					:field="entry.field"
				/>
			</template>

			<template #render="slotProps">
				<slot
					name="field-render"
					:field="entry.field"
					v-bind="slotProps"
				/>
			</template>
		</FormSchemaField>

		<BlockRender
			v-else
			:key="`${entry.type}-${entry.key}`"
			:block="entry.block"
			:props="entry.props"
		>
			<template
				v-for="(slot, name) in entry.slots"
				:key="name"
				#[name]="slotProps"
			>
				<RenderFnWrapper :render-fn="() => slot(slotProps)" />
			</template>

			<template
				v-if="entry.type === 'section'"
				#[entry.slot]
			>
				<NestedFields :nested="{ schema: entry.fields }" />
			</template>

			<template
				v-for="section in entry.type === 'sections' ? entry.namedSections : []"
				:key="section.slotName"
				#[section.slotName]
			>
				<NestedFields :nested="{ schema: section.item.section.fields }" />
			</template>

			<template
				v-if="entry.type === 'sections' && entry.singleSlot"
				#[entry.singleSlot.name]="slotProps"
			>
				<RenderFnWrapper :render-fn="() => renderSingleSlot(entry, slotProps)" />
			</template>
		</BlockRender>
	</template>
</template>
